import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Flame, Trash2 } from "lucide-react";
import { format, subDays } from "date-fns";
import { toast } from "sonner";

export default function Habits() {
  const [habits, setHabits] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const load = async () => setHabits((await api.get("/habits")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    await api.post("/habits", { name });
    setName(""); setOpen(false); load();
  };
  const check = async (h, date, done) => {
    await api.post(`/habits/${h.habit_id}/checkin`, { date, done });
    load();
    if (done) toast.success("+8 XP");
  };
  const del = async (h) => {
    if (!window.confirm("Delete habit?")) return;
    await api.delete(`/habits/${h.habit_id}`);
    load();
  };

  const days = [...Array(90)].map((_, i) => format(subDays(new Date(), 89 - i), "yyyy-MM-dd"));

  return (
    <div className="min-h-screen px-10 pt-10 pb-16">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="serif-italic text-5xl text-white/85">Habits</h1>
        <button className="pill" onClick={() => setOpen(true)} data-testid="new-habit" style={{ background: "var(--orange)", borderColor: "var(--orange)" }}><Plus size={13} /> New habit</button>
      </div>
      <div className="h-px bg-white/10 mb-8" />

      {habits.length === 0 ? (
        <div className="text-center mt-16 serif-italic text-2xl text-white/60">Small habits, big lives.</div>
      ) : (
        <div className="space-y-4">
          {habits.map(h => {
            const doneSet = new Set(h.checkins.filter(c => c.done).map(c => c.date));
            const today = format(new Date(), "yyyy-MM-dd");
            return (
              <div key={h.habit_id} className="card-base p-5" data-testid={`habit-${h.habit_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full grid place-items-center" style={{ background: h.color + "33", color: h.color }}>
                      <Flame size={16} />
                    </div>
                    <div>
                      <div className="text-white/90">{h.name}</div>
                      <div className="text-xs text-white/40">{h.streak} day streak</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button size="sm" onClick={() => check(h, today, !doneSet.has(today))} variant={doneSet.has(today) ? "default" : "outline"} data-testid={`habit-checkin-${h.habit_id}`} style={doneSet.has(today) ? { background: "var(--orange)" } : {}}>
                      {doneSet.has(today) ? "Done today" : "Mark today"}
                    </Button>
                    <button onClick={() => del(h)} className="text-white/30 hover:text-white/70"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="flex gap-[3px] flex-wrap">
                  {days.map(d => {
                    const level = doneSet.has(d) ? "l4" : "";
                    return <div key={d} className={`heat ${level}`} title={d} />;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--surface)] border-white/10">
          <DialogHeader><DialogTitle className="serif-italic text-2xl">New habit</DialogTitle></DialogHeader>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Read 20 pages" className="bg-white/5 border-white/10" data-testid="habit-name" />
          <div className="flex justify-end"><Button onClick={create} style={{ background: "var(--orange)" }} data-testid="habit-save">Save</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
